<template>
    <div>

        <vue-headful title="Inspection Results :: MeritEHS"/>

        <h4 class="py-3 mb-3">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active">
                    <i class="fa fa-list"></i> Inspection Results
                </li>
            </ol>
        </h4>

        <div class="card">
            <div class="card-body">
                <v-server-table name="inspResultTable" ref="inspResultTable" url="/reports/inspection-results-list" :columns="columns" :options="options">

                    <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 mb-md-0 mb-3 float-md-left">
                        <div class="clearfix d-md-none d-block"></div>

                        <div class="float-md-left">
                            <div class="float-left">
                                <table-filters-2 ref="inspResultTableFilters" v-bind:filters="filters"
                                filterUrl="/reports/filters/inspections"
                                :filterKey="filterKey"
                                :emptyFilters="filterDefs"
                                @filtersChanged="refreshTableData"
                                @filtersCleared="filtersCleared">
                                    <template slot-scope="slotProps">
                                        <div class="d-inline-block">
                                            <multi-select v-model="slotProps.filters['jobsite.DivisionId']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="slotProps.filterData.divisions" :options="{multi: true}"></multi-select>
                                        </div>
                                        <div class="d-inline-block">
                                            <multi-select v-model="slotProps.filters['inspectionresult.ComplianceStatusId']" :btnLabel="(values) => ('Compliance ('+values.length+')')" :selectOptions="slotProps.filterData.compliance" :options="{multi: true}"></multi-select>
                                        </div>
                                        <div class="d-inline-block">
                                            <date-range-selector buttonTitle="Inspection Date" v-model="slotProps.filters['inspection.InspectionDate']" />
                                        </div>
                                        <div class="d-inline-block">
                                            <multi-select v-model="slotProps.filters['inspection.inspection_form_id']" :btnLabel="(values) => ('Inspection Forms ('+values.length+')')" :selectOptions="slotProps.filterData.formOptions" :options="{multi: true}"></multi-select>
                                        </div>
                                        <div class="d-inline-block">
                                            <multi-select v-model="slotProps.filters['jobsite.JobsiteId']" :btnLabel="(values) => ('Jobsites ('+values.length+')')" :selectOptions="slotProps.filterData.jobsites" :options="{multi: true}" :search="true"></multi-select>
                                        </div>
                                    </template>
                                </table-filters-2>
                            </div>

                            <button type="button" class="btn btn-outline-success ml-2" v-on:click="exportTable()" :disabled="exportingTable">
                                <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                                <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                            </button>

                            <div class="clearfix d-md-none d-block"></div>
                        </div>

                        <div class="clearfix d-md-none d-block"></div>

                        <div class="float-md-left ml-2 pt-1" v-if="loading">
                            <i class="fa fa-spin fa-spinner"></i> Loading Inspection Results
                        </div>
                    </div>

                    <template slot="InspectionId" slot-scope="props">
                        <router-link :to="`/inspections/view/${props.row.InspectionId}`" title="View Inspection" class="btn btn-xs btn-light" v-if="false">
                            View
                        </router-link>

                        <b-dropdown html='<i class="fa fa-ellipsis-h"></i>' variant="default" :no-flip="true" size="sm">
                            <b-dropdown-item href="javascript:void(0)" v-on:click="$router.push(`/inspections/view/${props.row.InspectionId}`)" title="View Inspection">
                                View INSP# {{ props.row.InspectionId }}
                            </b-dropdown-item>
                            <b-dropdown-item href="javascript:void(0)" v-on:click="$router.push(`/inspections/result/${props.row.InspectionResultId}`)" title="View Inspection Result">
                                View Result# {{ props.row.sectionDisplayOrder }}.{{ props.row.DisplayOrder }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </template>

                    <template slot="DivisionName" slot-scope="props">
                        <span v-if="props.row.division_code">{{ props.row.division_code }} -</span> {{ props.row.DivisionName }}
                    </template>

                    <router-link slot="JobsiteName" slot-scope="props" :to="`/jobsites/view/${props.row.JobsiteId}`" title="View Jobsite">
                        {{ props.row.JobsiteNumber }} - {{ props.row.JobsiteName }}
                    </router-link>

                    <template slot="owner_last_name" slot-scope="props">
                        <template v-if="props.row.hide_creator == 0 || $store.state.user.is_admin == true">
                            {{ props.row.owner_first_name }} {{ props.row.owner_last_name }} <template v-if="props.row.owner_employee_number">- {{ props.row.owner_employee_number }}</template>
                        </template>
                    </template>

                    <template slot="DisplayOrder" slot-scope="props">
                        {{ props.row.sectionDisplayOrder }}.{{ props.row.DisplayOrder }}
                    </template>

                    <template slot="SectionName" slot-scope="props">
                        {{ props.row.sectionDisplayOrder }}. {{ props.row.SectionName }}
                    </template>

                    <template slot="ElementText" slot-scope="props">
                        {{ props.row.DisplayOrder }}. {{ props.row.ElementText }}
                    </template>

                    <template slot="ComplianceStatusName" slot-scope="props">
                        <label :class="`badge badge-${getComplianceColor(props.row)}`">{{ getComplianceLabel(props.row) }}</label>
                    </template>

                    <template slot="InspectionDate" slot-scope="props">
                        {{ props.row.InspectionDate | moment("MM/DD/YYYY") }}
                    </template>

                    <template slot="child_row" slot-scope="props">
                        <label class="text-muted">Comment:</label><br/>
                        {{ props.row.Comment }}

                        <custom-field-table-display :custom_fields="props.row.custom_fields"></custom-field-table-display>
                    </template>

                </v-server-table>
            </div>
        </div>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';

    export default {
        props: {
            allowFilterSaving: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                loading: false,
                columns: ['InspectionId', 'DivisionName', 'JobsiteName', 'owner_last_name', 'DisplayOrder', 'SectionName', 'ElementText', 'ComplianceStatusName', 'InspectionDate'],
                options: {
                    headings: {
                        InspectionId: 'Actions',
                        DivisionName: 'Division',
                        JobsiteName: 'Jobsite',
                        owner_last_name: 'Creator',
                        DisplayOrder: 'Item',
                        SectionName: 'Section',
                        ElementText: 'Element',
                        ComplianceStatusName: 'Compliance',
                        InspectionDate: 'Date'
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        //this.$emit('noncompliantListCount', response.data.count);

                        //HIde the Loading Indicator
                        this.loading = false;

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Set the filters
                        data.filters = filterExtractor.extractFilters(this.filters);

                        //Show the Loading Indicator
                        this.loading = true;

                        //Send it
                        return data;
                    },
                    uniqueKey: 'InspectionResultId',
                    orderBy: {
                        ascending: false,
                        column: 'InspectionDate',
                    }
                },
                jobsites: [],
                isLoading: false,
                exportingTable: false,

                inspection_form_id: null,

                filterKey: 'inspResultFilters',

                filterDefs: {
                    'inspection.InspectionDate': 'ytd',
                    'jobsite.DivisionId': [],
                    'jobsite.JobsiteId': [],
                    'inspectionresult.ComplianceStatusId': [],
                    'inspection.inspection_form_id': [],
                },
            }
        },

        watch: {
            appliedFilter(newVal, oldVal){
                //Call the helper
                this.checkForAppliedFilter();
            },
        },

        computed: {
            filters(){
                //Check State
                if(this.$store.state.tableFilters.filters[this.filterKey])
                {
                    //Send it
                    return this.$store.state.tableFilters.filters[this.filterKey];
                }

                //Return Definition
                return JSON.parse(JSON.stringify(this.filterDefs));
            },

            appliedFilter(){
                //Send it
                return this.$store.state.appliedFilters[this.filterKey] ? this.$store.state.appliedFilters[this.filterKey] : null;
            },
        },

        methods: {

            getComplianceLabel(result){
                //Check null
                if(!result.result_labels)
                {
                    return result.ComplianceStatusName;
                }

                //Decode
                var labels = JSON.parse(result.result_labels);

                //Check if SET
                if(result.ComplianceStatusId == 1 && labels['compliant'])
                {
                    return labels['compliant'];
                }
                else if(result.ComplianceStatusId == 2 && labels['noncompliant'])
                {
                    return labels['noncompliant'];
                }
                else if(result.ComplianceStatusId == 3 && labels['na'])
                {
                    return labels['na'];
                }

                return result.ComplianceStatusName;
            },

            getComplianceColor(result){
                //SEtup
                var color = 'secondary';

                //Handle it
                switch(result.ComplianceStatusId)
                {
                    case 1:
                        //Set it
                        color = 'success';
                        break;

                    case 2:
                        //Set it
                        color = 'danger';
                        break;
                }

                //SEnd it
                return color;
            },

            refreshTableData(filters) {
                //Set the data
                this.$refs.inspResultTable.setPage(1); //This automatically calls get Data
                //this.$refs.inspResultTable.getData();
            },

            filtersCleared(){
                //Reset
                this.refreshTableData();

                //Reset the Columns
                this.$refs.inspResultTableFilters.resetColVis(this.$refs.inspResultTable);
            },

            setUserFilterData(){
                //Call the Helper
                this.$refs.nonCompliantSavedFilters.setFilterParams(this.$refs.inspResultTable);
            },

            checkForAppliedFilter(){
                //Check if
                if(!this.allowFilterSaving || !this.appliedFilter)
                {
                    return;
                }

                //Check for the Inspection FORM ID and Make sure to remove it from the filters
                var config = JSON.parse(this.appliedFilter.table_config);

                //Call the helper
                this.$refs.inspResultTableFilters.setColVis(this.$refs.inspResultTable);
            },

            limitText (count) {
                return `and ${count} other jobsites`;
            },

            asyncFind (query) {
                this.isLoading = true;
                this.$http
                    .get('jobsites/search', {term: query})
                    .then(response => {
                        this.jobsites = response.data;
                        this.isLoading = false;
                    })
                    .catch(response => {

                    });
            },

            exportTable(){
                //Loading indicator
                this.exportingTable = true;

                //Build the Filters
                var filters = filterExtractor.extractFilters(this.filters);

                //Setup Columns
                var cols = JSON.parse(JSON.stringify(this.$refs.inspResultTable.columns));

                //Add the Comment Column Manually
                cols.push('Comment');

                //Build the payload
                var payload = {
                    filters: filters,
                    query: (this.$refs.inspResultTable.getRequestParams()).query,
                    orderBy: this.$refs.inspResultTable.options.orderBy.column,
                    ascending: this.$refs.inspResultTable.options.orderBy.ascending,
                    table: 'inspResultTable',
                    columns: cols,
                    //headings: this.$refs.inspResultTable.options.headings,
                    load_custom_fields: true,
                    custom_field_type: 'Employee',
                };

                //Send it
                this.$http.get('/reports/inspection-results-list?export=true', payload)
                    .then(response => {
                        //Open the new window
                        //window.open(response.data);

                        //Alert the User
                        this.$toastr.success(response.data);
                    })
                    .catch(err => {
                        //Alert the user
                        this.$toastr.error(err.response.data, 'Failed to Export Inspection Results');
                    })
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },
        },

        mounted() {
            //Check for a saved filter
            this.checkForAppliedFilter();

            //Focus on the SEarch Input
            //this.$refs.reportNonCompliantList.$refs.inspResultTable.$refs.genericFilter.focus();
        }
    }
</script>
